import React, { useState, useMemo } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { ItemAlmacen3D, CapacidadRackInfo, RackSeleccionadoData, SlotInfo } from './tipos';
import { SlotRack3D } from './SlotRack3D';

interface Rack3DProps {
  id?: string;
  zona: string;
  pasillo: string;
  rack: string;
  items: ItemAlmacen3D[];
  capacidad: CapacidadRackInfo;
  codigoVisual: string;
  position: [number, number, number];
  rotacion?: number; // en grados: 0, 90, 180, 270
  ancho?: number; // ancho en metros (ej: 2.8m o 4.0m)
  nivelesOverride?: number;
  isSelected?: boolean;
  modoArquitecto?: boolean;
  isModoReubicacion?: boolean;
  slotOrigenReubicacionId?: string | null;
  onSelectRack: (data: RackSeleccionadoData) => void;
  onSelectForEdit?: (id: string) => void;
  onSelectSlot?: (slot: SlotInfo) => void;
}

export const Rack3D: React.FC<Rack3DProps> = ({
  id,
  zona,
  pasillo,
  rack,
  items,
  capacidad,
  codigoVisual,
  position,
  rotacion = 0,
  ancho = 2.8,
  nivelesOverride,
  isSelected = false,
  modoArquitecto = false,
  isModoReubicacion = false,
  slotOrigenReubicacionId = null,
  onSelectRack,
  onSelectForEdit,
  onSelectSlot,
}) => {
  const [hovered, setHovered] = useState(false);

  // Cálculos logísticos agregados del rack
  const currentTon = useMemo(() => {
    return (
      items.reduce(
        (acc, item) => acc + (Number(item.peso) || 0) * (Number(item.cantidad) || 0),
        0
      ) / 1000
    );
  }, [items]);

  const maxTonCap = capacidad.max_ton || 1;
  const occupancyPercent = useMemo(() => {
    return Math.min(Math.round((currentTon / maxTonCap) * 100), 100);
  }, [currentTon, maxTonCap]);

  const isOverloaded = occupancyPercent >= 100 || currentTon >= maxTonCap;

  // Dimensiones del rack (en metros)
  const rackWidth = ancho || 2.8;
  const rackDepth = 1.05;
  const niveles = nivelesOverride || capacidad.niveles || 3;
  const nivelHeight = 1.35;
  const totalHeight = niveles * nivelHeight + 0.3;

  // Número de posiciones por nivel (sincronizado con capacidad y el mapa del modal de 8 posiciones)
  const maxPosInItems = useMemo(() => {
    return Math.max(0, ...items.map((it) => parseInt(String(it.posicion || '1').replace(/\D/g, ''), 10) || 1));
  }, [items]);

  const slotsPorNivel = useMemo(() => {
    const capPos = (capacidad as any)?.posiciones_por_nivel || 8;
    return Math.max(capPos, maxPosInItems, 2);
  }, [capacidad, maxPosInItems]);

  // Alturas de vigas horizontales
  const beamHeights = useMemo(() => {
    return Array.from({ length: niveles }).map((_, i) => (i + 1) * nivelHeight - 0.2);
  }, [niveles, nivelHeight]);

  // Posiciones X de slots dentro de cada nivel (distribución uniforme de hasta 8 posiciones)
  const slotXPositions = useMemo(() => {
    const positions: number[] = [];
    const innerWidth = rackWidth * 0.94;
    const step = innerWidth / slotsPorNivel;
    const startX = -innerWidth / 2 + step / 2;
    for (let i = 0; i < slotsPorNivel; i++) {
      positions.push(Number((startX + i * step).toFixed(3)));
    }
    return positions;
  }, [slotsPorNivel, rackWidth]);

  // Mapeo granular de cada hueco/slot individual
  const slotSlots: SlotInfo[] = useMemo(() => {
    const slotsArr: SlotInfo[] = [];

    for (let n = 1; n <= niveles; n++) {
      for (let s = 1; s <= slotsPorNivel; s++) {
        // Encontrar items asignados a este nivel y posición específica (ej: N03, P07)
        const itemsEnSlot = items.filter((it) => {
          const itemNivel = parseInt(String(it.nivel || '1').replace(/\D/g, ''), 10) || 1;
          const itemPos = parseInt(String(it.posicion || '1').replace(/\D/g, ''), 10) || 1;
          return itemNivel === n && itemPos === s;
        });

        const isOccupied = itemsEnSlot.length > 0;
        const cant = itemsEnSlot.reduce((sum, i) => sum + (Number(i.cantidad) || 0), 0);
        const pesoKg = itemsEnSlot.reduce(
          (sum, i) => sum + (Number(i.peso) || 0) * (Number(i.cantidad) || 0),
          0
        );
        const pesoTon = pesoKg / 1000;
        const lpn = itemsEnSlot[0]?.lote
          ? `LPN-${itemsEnSlot[0].lote}`
          : itemsEnSlot[0]?.sku
          ? `LPN-${itemsEnSlot[0].sku}`
          : `LPN-${zona}${rack.replace(/\D/g, '') || '01'}-N${n}S${s}`;

        const estadoPrincipal = itemsEnSlot.some(
          (i) => i.estado === 'Dañado' || i.estado === 'Cuarentena'
        )
          ? 'Dañado'
          : itemsEnSlot.some((i) => i.estado === 'Reservado')
          ? 'Reservado'
          : 'Disponible';

        const slotKey = `${zona}-${pasillo}-${rack}-N${n}-S${s}`;

        slotsArr.push({
          slotKey,
          zona,
          pasillo,
          rack,
          nivel: n,
          posicion: s,
          lpn,
          items: itemsEnSlot,
          pesoTotalKg: pesoKg,
          pesoTotalTon: pesoTon,
          cantidadTotal: cant,
          estadoPrincipal,
          isOccupied,
        });
      }
    }
    return slotsArr;
  }, [items, niveles, slotsPorNivel, zona, pasillo, rack]);

  const rotationRadians = (rotacion * Math.PI) / 180;

  return (
    <group
      position={position}
      rotation={[0, rotationRadians, 0]}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
        document.body.style.cursor = 'auto';
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (modoArquitecto && onSelectForEdit && id) {
          onSelectForEdit(id);
        } else if (!modoArquitecto && !isModoReubicacion) {
          onSelectRack({
            nombre: codigoVisual,
            zona,
            pasillo,
            rack,
            items,
            niveles,
            ancho: rackWidth,
          });
        }
      }}
    >
      {/* 1. HITBOX INVISIBLE */}
      <mesh position={[0, totalHeight / 2, 0]} visible={false}>
        <boxGeometry args={[rackWidth + 0.4, totalHeight + 0.6, rackDepth + 0.4]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* 2. EFECTO VISUAL DE SELECCIÓN EN MODO ARQUITECTO */}
      {isSelected && (
        <group position={[0, totalHeight / 2, 0]}>
          <mesh>
            <boxGeometry args={[rackWidth + 0.22, totalHeight + 0.22, rackDepth + 0.22]} />
            <meshBasicMaterial color="#06b6d4" wireframe />
          </mesh>
          <mesh>
            <boxGeometry args={[rackWidth + 0.2, totalHeight + 0.2, rackDepth + 0.2]} />
            <meshBasicMaterial color="#06b6d4" transparent opacity={0.12} />
          </mesh>
          {[-rackWidth / 2 - 0.1, rackWidth / 2 + 0.1].map((cx, cxi) =>
            [-totalHeight / 2 - 0.1, totalHeight / 2 + 0.1].map((cy, cyi) =>
              [-rackDepth / 2 - 0.1, rackDepth / 2 + 0.1].map((cz, czi) => (
                <mesh key={`corner-${cxi}-${cyi}-${czi}`} position={[cx, cy, cz]}>
                  <boxGeometry args={[0.08, 0.08, 0.08]} />
                  <meshBasicMaterial color="#38bdf8" />
                </mesh>
              ))
            )
          )}
        </group>
      )}

      {/* 3. PLACA BASE / PROTECTOR DE IMPACTO AMARILLO EN ESQUINAS */}
      {[-rackWidth / 2, rackWidth / 2].map((x, xi) =>
        [-rackDepth / 2, rackDepth / 2].map((z, zi) => (
          <mesh key={`guard-${xi}-${zi}`} position={[x, 0.15, z]}>
            <cylinderGeometry args={[0.08, 0.09, 0.3, 8]} />
            <meshStandardMaterial color="#facc15" roughness={0.4} metalness={0.2} />
          </mesh>
        ))
      )}

      {/* 4. PUNTALES VERTICALES (Bastidores Azules Industriales) */}
      {[-rackWidth / 2, rackWidth / 2].map((x, xi) =>
        [-rackDepth / 2, rackDepth / 2].map((z, zi) => (
          <mesh
            key={`post-${xi}-${zi}`}
            position={[x, totalHeight / 2, z]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[0.08, totalHeight, 0.08]} />
            <meshStandardMaterial
              color={isSelected ? '#38bdf8' : hovered ? '#3b82f6' : '#1e3a8a'}
              metalness={0.6}
              roughness={0.3}
            />
          </mesh>
        ))
      )}

      {/* 5. CELOSÍAS / DIAGONALES DE REFUERZO LATERAL */}
      {[-rackWidth / 2, rackWidth / 2].map((x, xi) => (
        <group key={`bracing-frame-${xi}`} position={[x, totalHeight / 2, 0]}>
          {Array.from({ length: niveles }).map((_, ni) => (
            <mesh
              key={`diag-${ni}`}
              position={[0, (ni - niveles / 2 + 0.5) * nivelHeight, 0]}
              rotation={[0, 0, (ni % 2 === 0 ? 1 : -1) * 0.4]}
            >
              <boxGeometry args={[0.03, nivelHeight * 1.05, 0.03]} />
              <meshStandardMaterial color="#1e40af" metalness={0.5} roughness={0.4} />
            </mesh>
          ))}
        </group>
      ))}

      {/* 6. LARGUEROS HORIZONTALES (Vigas Naranjas de Carga) */}
      {beamHeights.map((y, bi) => (
        <group key={`level-${bi}`}>
          {/* Viga frontal (Z positiva) */}
          <mesh position={[0, y, rackDepth / 2]} castShadow receiveShadow>
            <boxGeometry args={[rackWidth, 0.09, 0.05]} />
            <meshStandardMaterial
              color={isSelected ? '#f97316' : hovered ? '#fb923c' : '#ea580c'}
              metalness={0.4}
              roughness={0.35}
            />
          </mesh>

          {/* Viga trasera (Z negativa) */}
          <mesh position={[0, y, -rackDepth / 2]} castShadow receiveShadow>
            <boxGeometry args={[rackWidth, 0.09, 0.05]} />
            <meshStandardMaterial
              color={isSelected ? '#f97316' : hovered ? '#fb923c' : '#ea580c'}
              metalness={0.4}
              roughness={0.35}
            />
          </mesh>

          {/* Travesaños de apoyo para pallets */}
          {[-rackWidth * 0.3, 0, rackWidth * 0.3].map((crossX, ci) => (
            <mesh key={`cross-${ci}`} position={[crossX, y + 0.02, 0]}>
              <boxGeometry args={[0.04, 0.04, rackDepth]} />
              <meshStandardMaterial color="#94a3b8" metalness={0.7} roughness={0.3} />
            </mesh>
          ))}
        </group>
      ))}

      {/* 7. SLOTS GRANULARES 3D (Celdas individuales con Pallet o Hueco Libre) */}
      {slotSlots.map((slot) => {
        const nivelIdx = slot.nivel - 1;
        const slotIdx = slot.posicion - 1;
        const posX = slotXPositions[slotIdx];
        const posY = beamHeights[nivelIdx] + 0.05;

        return (
          <SlotRack3D
            key={slot.slotKey}
            slot={slot}
            position={[posX, posY, 0]}
            anchoSlot={rackWidth / slotsPorNivel}
            altoSlot={nivelHeight}
            profundidadSlot={rackDepth}
            isModoReubicacion={isModoReubicacion}
            isSlotOrigen={slotOrigenReubicacionId === slot.slotKey}
            onSelectSlot={(s) => {
              if (onSelectSlot && !modoArquitecto) {
                onSelectSlot(s);
              }
            }}
          />
        );
      })}

      {/* 8. CARTEL / IDENTIFICADOR DE RACK (Letrero WMS en el tope) */}
      <Billboard
        position={[0, totalHeight + 0.38, 0]}
        onClick={(e) => {
          e.stopPropagation();
          if (modoArquitecto && onSelectForEdit && id) {
            onSelectForEdit(id);
          } else if (!modoArquitecto && !isModoReubicacion) {
            onSelectRack({
              nombre: codigoVisual,
              zona,
              pasillo,
              rack,
              items,
              niveles,
              ancho: rackWidth,
            });
          }
        }}
      >
        <mesh position={[0, 0, -0.01]}>
          <planeGeometry args={[1.25, 0.34]} />
          <meshBasicMaterial
            color={isSelected ? '#0284c7' : '#09090b'}
            opacity={0.92}
            transparent
          />
        </mesh>
        <Text
          fontSize={0.16}
          color={isSelected ? '#ffffff' : isOverloaded ? '#fb7185' : '#f8fafc'}
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          {isSelected ? `✏️ ${rack}` : rack}
        </Text>
      </Billboard>
    </group>
  );
};
